import { CSSProperties, useState } from 'react';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { UrlParams } from 'common/types';
import { DashboardOld, Host } from 'common/graphqlTypes';
import { buildUrl } from 'common/util/location';

import Button from 'components/common/Button/Button';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import Link from 'components/common/Link/Link';

import './LogOverviewSourcesRow.scss';

const getLogFileLink = (serverHost: string, logFile: string): string =>
  buildUrl(K.ROUTE.EVENTS, {
    [K.URL_PARAM.LOG_SOURCE]: serverHost,
    [K.URL_PARAM.LOG]: logFile,
  } as UrlParams);

/**
 * Get list of the log files for a source.
 * @param sourceName {string} - the name of the source
 * @param logFiles {Array[string]} - show a list of the log files where each item is to view logs
 * for the source file.
 * @param allShown {Boolean} - track if all files should be shown. We only show 4 by default.
 */
const getLogs = (sourceName: string, logFiles: string[], allShown: boolean, index: number): JSX.Element[] => {
  const logsToShow = allShown ? logFiles.length : 4;
  const sortedLogFiles = [...logFiles].sort();
  return sortedLogFiles.slice(0, logsToShow).map((logFile, logIndex) => (
    <Link
      href={getLogFileLink(sourceName, logFile)}
      className="LogOverviewSourcesRow__FileLink link"
      key={sourceName + '|' + logFile}
      testId={createTestId(ELEMENT.overviewLogfile, index, logIndex)}
    >
      {logFile}
    </Link>
  ));
};

/**
 * Get link to view the logs for a given source name.
 * @param sourceName {string} - source name. It maps to the $serverHost query field.
 */
const getSourceNameLink = (sourceName: string): string =>
  buildUrl(K.ROUTE.EVENTS, {
    [K.URL_PARAM.LOG_SOURCE]: sourceName,
  } as UrlParams);

/**
 * Get link to view the logs for a given source name.
 * @param dashboardUrlTemplate {string} - the ser
 * @param serverHost {string} - the name of the server host
 * TODO: refactor once the api returns the correct urlTemplate
 */
const getDashboardUrl = (dashboardUrlTemplate: string, serverHost: string): string => {
  const encodedServerHost = encodeURIComponent(serverHost);
  return dashboardUrlTemplate.replace('__SERVER_HOST__', encodedServerHost).replace('&param_', '&params=');
};

/**
 * Get list of the dashboards where each item is a link to the dashboard
 * @param dashboards {Array[string]} - list of dashboard names.
 * @param serverHost {string} - the name of the server host
 */

const getDashboards = (dashboards: DashboardOld[] = [], serverHost: string, index: number): JSX.Element[] =>
  dashboards.map((dashboard: DashboardOld) => (
    <Link
      href={getDashboardUrl(dashboard.urlTemplate, serverHost)}
      className="LogOverviewSourcesRow__DashboardLink link"
      key={serverHost + '|' + dashboard.name}
      testId={createTestId(ELEMENT.overviewDashboard, index, dashboard.name.toLowerCase().replace(/\s+/g, '-'))}
    >
      {dashboard.name}
    </Link>
  ));

type LogOverviewSourcesRowProps = {
  source: Host;
  index: number;
  style: CSSProperties;
};

/**
 * This component creates an individual row for a host. By default we show only four log files per host but the user
 * has the ability to click "show more" to see the remainder.
 *
 * @prop source    data for the row
 * @prop index     index within containing list
 */
function LogOverviewSourcesRow({ source, index, style }: LogOverviewSourcesRowProps): JSX.Element {
  const [allLogsShown, setAllLogsShown] = useState(false);
  let showMoreButton;
  if (source.logFiles.length > 4) {
    const showLogsTestId = createTestId(ELEMENT.overviewShowLogs, index);
    showMoreButton = (
      <Button size="sm" onClick={(): void => setAllLogsShown(!allLogsShown)} testId={showLogsTestId}>
        {allLogsShown ? 'Show Less' : `Show All Logs (${source.logFiles.length})`}
      </Button>
    );
  }

  const sourceTooltipMessage = Object.entries(source.attributes || {})
    .map(([key, value]) => ` ${key}=${value}`)
    .toString()
    .trim();

  const sourceNameTestId = createTestId(ELEMENT.overviewSourceName, index);
  const sourceNameLink = source.attributes ? (
    <ElementWithTooltip placement="right" tooltip={sourceTooltipMessage}>
      <span>
        <Link
          href={getSourceNameLink(source.name)}
          className="LogOverviewSourcesRow__SourceName link"
          testId={sourceNameTestId}
        >
          {source.name}
        </Link>
      </span>
    </ElementWithTooltip>
  ) : (
    <Link
      href={getSourceNameLink(source.name)}
      className="LogOverviewSourcesRow__SourceName link"
      testId={sourceNameTestId}
    >
      {source.name}
    </Link>
  );

  return (
    <div className="LogOverviewSourcesRow" style={style}>
      <div className="LogOverviewSourcesRow__td LogOverviewSourcesRow__Source">
        {sourceNameLink}
        <div className="LogOverviewSourcesRow__SourceIP">{source.ipAddress || ''}</div>
      </div>

      <div className="LogOverviewSourcesRow__td LogOverviewSourcesRow__Logs">
        {getLogs(source.name, source.logFiles, allLogsShown, index)}
        <br />
        {showMoreButton}
      </div>

      <div className="LogOverviewSourcesRow__td LogOverviewSourcesRow__Dashboards">
        {getDashboards(source.dashboards, source.name, index)}
      </div>
    </div>
  );
}

export default LogOverviewSourcesRow;
